import { getBaseThemeName } from "@/features/branding";
import { useMaxAssetCountForCollectionHome } from "@/features/collection_home/utils";
import { InteractiveTutorial, InteractiveTutorialProps } from "@/features/interactive_tutorial";
import { usePortfolioTutorialsState, type TutorialStepId } from "@/tools/aggregate/portfolio-export/PortfolioTutorialContext";
import { useAvailablePortfolioTypes } from "@/utils/hooks/useAvailablePortfolioTypes";
import { BellOutlined, MoreOutlined } from "@ant-design/icons";
import { Alert, Image, TourStepProps } from "antd";
import Link from "next/link";
import { useTranslation } from "react-i18next";
export interface PortfolioInsightsTutorial extends InteractiveTutorialProps<Record<any, any>> {
  refs?: undefined;
}
type TutorialStep = {
  id: TutorialStepId;
  step: TourStepProps;
};
export const PortfolioInsightsTutorial = ({
  ...props
}: PortfolioInsightsTutorial) => {
  const {
    canRunAutoAdaptation,
    newRunModalTrigger,
    newRunModalUpload,
    newRunModalReview,
    setIsTutorialNewRunModalOpen,
    setTutorialNewRunState,
    newRunModalTypeSelection,
    analysisRun,
    setTutorialSchemaForStep,
    alertBellTrigger,
    listFilters,
    handleSummaryStep,
    handleSummaryGenerated,
    setShowDownloadDropdown,
    setSelectedDownloadItemIndex,
    downloadsMenuPopup,
    setShowPointLevelResultsModal,
    setShowSummaryModal,
    portfolioItem,
    summaryQueueModal,
    outputTypeModal,
    editModal,
    deleteModal,
    setShowMoreDropdown,
    setShowTutorialEditModal,
    setShowTutorialDeleteModal
  } = usePortfolioTutorialsState();
  const maxAssetCount = useMaxAssetCountForCollectionHome();
  const {
    t: remoteJobs
  } = useTranslation("tools", {
    keyPrefix: "comprehensive.remoteJobs"
  });
  const {
    t: terms
  } = useTranslation("terms");
  const {
    t: settings
  } = useTranslation("tools", {
    keyPrefix: "settings"
  });
  const hasMultiplePortfolioTypes = useAvailablePortfolioTypes().length > 1;

  /** Falls back to the trigger until the menu has mounted and been published. */
  const downloadsMenuTarget = () => downloadsMenuPopup?.current || portfolioItem?.downloadsMenu?.current || null;
  const landingSteps: TutorialStep[] = [{
    id: "landing",
    step: {
      title: "",
      description: <>
            <p>
              On the main navigation menu, under <b>&lsquo;Comprehensive Analysis Tools&rsquo;</b>, Click on{" "}
              <b>&lsquo;Portfolio Analysis&rsquo;</b>. You can access previous analysis from the Analysis library. Only
              those shared with you will be visible. Private analyses are only visible to those who create them.
            </p>
          </>
    }
  }];
  const newRunSteps: TutorialStep[] = [{
    id: "listFilters",
    step: {
      title: "",
      description: <>
            Use the filters above the Analysis Library to filter the list by user
            {hasMultiplePortfolioTypes ? ", group or portfolio type" : " or group"}.
          </>,
      target: () => listFilters?.current || null
    }
  }, {
    id: "startNewRun",
    step: {
      title: "",
      description: <>
            To start a new analysis, click <b>&lsquo;{remoteJobs("startNewRun")}&rsquo;</b>,{" "}
          </>,
      target: () => newRunModalTrigger?.current || null,
      placement: "left"
    }
  }];
  if (hasMultiplePortfolioTypes) {
    newRunSteps.push({
      id: "typeSelection",
      step: {
        title: "",
        description: <>
            Select the Portfolio Type you wish to run based on the inclusions and limitations of each option - Global or
            HKSAR.
          </>,
        target: () => newRunModalTypeSelection?.current || null
      }
    });
  }
  newRunSteps.push({
    id: "downloadTemplate",
    step: {
      title: "Download Upload Template",
      description: <>
            Download a portfolio upload template and enter your asset data into the template provided, being careful to
            follow the Upload Requirements.
          </>,
      target: () => newRunModalUpload?.uploadNewRun?.current || null
    }
  }, {
    id: "customiseTemplate",
    step: {
      title: "Customise Template",
      description: <>
            <b>&lsquo;Customise Template&rsquo;</b> provides additional asset configurability options such as elevation
            and extreme heat failure threshold for more advanced asset specificity.
          </>,
      target: () => newRunModalUpload?.uploadNewRun?.current || null
    }
  }, {
    id: "uploadCsv",
    step: {
      title: "Upload Your Portfolio",
      description: <>
            Once you have entered your asset details into the Portfolio Upload Template, save the template file to your
            desktop, then click <b>&lsquo;Select .csv file&rsquo;</b> to upload your newly created portfolio.
          </>,
      target: () => newRunModalUpload?.csvUpload?.current || null
    }
  }, {
    id: "reviewInfo",
    step: {
      title: "",
      description: <>
            A modal will appear confirming the details of your Portfolio Analysis. Here you can name the analysis,
            select sharing permissions and see a total asset count for your uploaded portfolio file. If you select{" "}
            <b>&lsquo;Private&rsquo;</b> your portfolio will be only visible to you, while <b>&lsquo;Shared&rsquo;</b>{" "}
            portfolios will be visible to other users in the selected group.
          </>,
      target: () => newRunModalReview?.info?.current || null
    }
  }, {
    id: "advancedSettings",
    step: {
      title: "",
      description: <>
            Under <b>&lsquo;{settings("advancedAnalysisSettings")}&rsquo;</b> choose whether to enable or disable{" "}
            {settings("anomalyManagementRp.title")} and {settings("defences.title")}. For more information about{" "}
            {settings("anomalyManagementRp.title")} or {settings("defences.title")}, visit the{" "}
            <Link href="/glossary/anomaly-management-1">Glossary</Link>.
          </>,
      target: () => newRunModalReview?.advancedSettings?.current || null
    }
  }, {
    id: "usage",
    step: {
      title: "",
      description: <>
            {hasMultiplePortfolioTypes ? <>
                Finally, review your current usage against the allocation available to your organisation. The allocation
                is separate for HKSAR Portfolios versus Global Portfolios.
              </> : <>Finally, review your current usage against the allocation available to your organisation.</>}
          </>,
      target: () => newRunModalReview?.usage?.current || null
    }
  }, {
    id: "runAnalysis",
    step: {
      title: "",
      description: <>
            Click <b>&lsquo;Run Analysis&rsquo;</b> to run the Portfolio, or <b>&lsquo;Back&rsquo;</b> to exit.
          </>,
      target: () => newRunModalReview?.runAnalysisTrigger?.current || null,
      placement: "top"
    }
  }, {
    id: "processingAlert",
    step: {
      title: "",
      description: <>
            The Portfolio will begin to run, with its status changed to blue and <b>&lsquo;Processing&rsquo;</b>.
          </>,
      target: () => analysisRun?.alert?.current || null
    }
  });
  if (canRunAutoAdaptation) {
    newRunSteps.push({
      id: "adaptationsRunning",
      step: {
        title: "",
        description: <>
            Once the portfolio has finished processing,{" "}
            <span className="lowercase">{terms("automatedResilience")}</span> runs begin automatically. After{" "}
            <span className="lowercase">{terms("automatedResilience")}</span> processing is complete, your results
            become available to download.
          </>,
        target: () => analysisRun?.item?.current || null
      }
    });
  }
  newRunSteps.push({
    id: "alertBell",
    step: {
      title: "",
      description: <>
          To enable a notification in your browser once the analysis is completed, click on the Alarm Bell icon{" "}
          <BellOutlined /> and follow the security prompts on your operating system to enable notifications.
        </>,
      target: () => alertBellTrigger?.current || null
    }
  });
  newRunSteps.push({
    id: "completedItem",
    step: {
      title: "",
      description: <>
          Once processing is complete, the status will change to a green tick.{" "}
          {hasMultiplePortfolioTypes && <>
              A tag beside the portfolio name will denote whether the portfolio type is &quot;HKSAR&quot; or
              &quot;Global&quot;.
            </>}
        </>,
      target: () => analysisRun?.item?.current || null
    }
  });
  const downloadSteps: TutorialStep[] = [{
    id: "summaryQueue",
    step: {
      title: "",
      description: <>
            <p>
              Your portfolio-level aggregated summary{" "}
              {canRunAutoAdaptation && <>
                  <span className="lowercase">and {terms("automatedResilience")}</span> aggregated summary{" "}
                </>}
              will automatically generate once your analysis is finished processing.
            </p>
            <p>
              In the top-right corner of the page, an out tray will appear showing your aggregated summary&rsquo;s
              position in the processing queue.
            </p>
            <div className="flex justify-center">
              <Image alt="Button that shows portfolio summary generation position in queue" src={`/img/tours/portfolio-insights/summary_modal_queue_trigger${getBaseThemeName() === "hkma" ? "_hkma" : ""}.png`} />
            </div>
            <p>Once it reaches the front of the queue, processing will begin automatically. </p>
            <Alert className="mb-4 mt-2 text-sm" description={<>
                  The time to generate will depend on the number of summaries (both within and outside your institution)
                  in queue and the size of those portfolios.
                </>} showIcon type="info" />
          </>,
      target: () => summaryQueueModal?.current || null
    }
  }, {
    id: "summaryGenerated",
    step: {
      title: "",
      description: <>
            <p>
              You will be notified that your aggregated summary is ready for download when the processing is complete.
            </p>
          </>,
      target: () => null,
      placement: "top"
    }
  }, {
    id: "downloadAggregated",
    step: {
      title: "",
      description: <>
            <p>
              To view an aggregated summary file that contains climate risk results aggregated across the entire
              portfolio, click <b>&lsquo;Download&rsquo;</b> then <b>&lsquo;Aggregated Summary Results&rsquo;</b> button
            </p>
            {canRunAutoAdaptation && <p>
                With {terms("automatedResilience")} enabled, this opens a list containing both the{" "}
                <b>&lsquo;{terms("adaptationTypes.baseline")}&rsquo;</b> and{" "}
                <b>&lsquo;{terms("adaptationTypes.enhanced_resilience")}&rsquo;</b> aggregated summaries.
              </p>}
          </>,
      target: downloadsMenuTarget,
      placement: "left"
    }
  }, {
    id: "downloadPointLevel",
    step: {
      title: "",
      description: <>
            <p>
              Click <b>&lsquo;Point-Level Results&rsquo;</b> to download a .csv file of metrics for each point within
              the portfolio.
            </p>
            {hasMultiplePortfolioTypes && <p>
                NOTE: HKSAR Portfolios only include Simple Risk Ratings, Significant Hazards &amp; MVAR at the
                point-level. Global Portfolios include all metrics at the point-level (including MVAR, FP, PL, TIP &amp;
                CAV).
              </p>}
          </>,
      target: downloadsMenuTarget,
      placement: "left"
    }
  }, {
    id: "outputTypeModal",
    step: {
      title: "",
      description: <>
            Select the type of result output and file type desired from the list of options provided. Once downloading
            is completed, the file will be saved to your downloads folder.
          </>,
      target: () => outputTypeModal?.current || null
    }
  }, {
    id: "downloadMetadata",
    step: {
      title: "",
      description: <>
            Click <b>&lsquo;Metadata Package&rsquo;</b> to download a zip file of relevant input settings and underlying
            data layers used to produce the climate risk results.
          </>,
      target: downloadsMenuTarget,
      placement: "left"
    }
  }];
  const actionsSteps: TutorialStep[] = [{
    id: "moreActions",
    step: {
      title: "",
      description: <>
            To edit or delete an existing Portfolio, click the vertical three-dot menu <MoreOutlined /> and select{" "}
            <b>&lsquo;Edit&rsquo;</b> or <b>&lsquo;Delete&rsquo;</b>.
          </>,
      target: () => portfolioItem?.actionsMenu?.current || null,
      placement: "top"
    }
  }, {
    id: "editModal",
    step: {
      title: "Edit",
      description: <>
            Update the name or sharing permissions of an existing Portfolio, and then click <b>&lsquo;Save&rsquo;</b>.
          </>,
      target: () => editModal?.current || null,
      placement: "bottom"
    }
  }, {
    id: "deleteModal",
    step: {
      title: "Delete",
      description: <>Deleting a portfolio will permanently remove it for all members of your organisational group.</>,
      target: () => deleteModal?.current || null
    }
  }, {
    id: "viewResults",
    step: {
      title: "View Results",
      description: <>
            <p>
              To view results on a dashboard click <b>&lsquo;View Results&rsquo;</b>. Results may take a few moments to
              load, depending on the size of the analysis.
            </p>
            <Alert className="mb-4 mt-2 text-sm" description={<>
                  Only analyses with {maxAssetCount.toLocaleString()} points or fewer can be visualised on the results
                  dashboard. For larger analyses, this option will be disabled.
                </>} showIcon type="info" />
          </>,
      target: () => portfolioItem?.viewResultsTrigger?.current || null,
      placement: "left"
    }
  }];
  const entries: TutorialStep[] = [...landingSteps, ...newRunSteps, ...downloadSteps, ...actionsSteps];

  // // TEMP(dev): only the download-menu steps while testing — restore the line below when done.
  // const entries: TutorialStep[] = [
  //   // ...landingSteps,
  //   //  ...newRunSteps,
  //   ...downloadSteps.filter(({ id }) =>
  //     (["downloadAggregated", "downloadPointLevel", "downloadMetadata"] as TutorialStepId[]).includes(id)
  //   ),
  //   ...actionsSteps,
  // ];
  // // const entries: TutorialStep[] = downloadSteps.filter(({ id }) =>
  // //   (["downloadAggregated", "downloadPointLevel", "downloadMetadata"] as TutorialStepId[]).includes(id)
  // // );
  // // TEMP(dev): keeps the skipped groups referenced so lint stays quiet.
  // void [landingSteps, newRunSteps, actionsSteps];

  /** Steps shown while the new run modal is open. */
  const newRunModalSteps: TutorialStepId[] = ["typeSelection", "downloadTemplate", "customiseTemplate", "uploadCsv", "reviewInfo", "advancedSettings", "usage", "runAnalysis"];
  const reviewSteps: TutorialStepId[] = ["reviewInfo", "advancedSettings", "usage", "runAnalysis"];
  /** Steps where the fake portfolio is mid-run, so the library renders it above the real list. */
  const runningSteps: TutorialStepId[] = ["processingAlert", "alertBell", "adaptationsRunning"];
  const downloadMenuSteps: TutorialStepId[] = ["downloadAggregated", "downloadPointLevel", "downloadMetadata"];
  return <InteractiveTutorial<Record<any, any>> steps={entries.map(({
    step
  }) => step)} isWide {...props} actionsRender={(originNode, {
    current,
    total
  }) => <div className="flex items-center gap-1">
          <span>
            {current + 1} / {total}
          </span>
          <div>{originNode}</div>
        </div>} onChange={(step: number) => {
    const stepId = entries[step]?.id;
    if (!stepId) return;
    setIsTutorialNewRunModalOpen(newRunModalSteps.includes(stepId));
    if (stepId === "typeSelection") {
      setTutorialNewRunState("TYPE_SELECTION");
    } else if (stepId === "customiseTemplate") {
      setTutorialNewRunState("CUSTOMISING");
    } else if (reviewSteps.includes(stepId)) {
      setTutorialNewRunState("IN_REVIEW");
    } else if (runningSteps.includes(stepId)) {
      setTutorialNewRunState("UPLOADING");
    } else {
      setTutorialNewRunState("INITIAL");
    }
    setTutorialSchemaForStep(stepId);
    setShowSummaryModal(stepId === "summaryQueue");
    if (stepId === "summaryGenerated") {
      handleSummaryStep();
    } else {
      handleSummaryGenerated();
    }
    setShowDownloadDropdown(downloadMenuSteps.includes(stepId));
    const downloadItemIndex = downloadMenuSteps.indexOf(stepId);
    setSelectedDownloadItemIndex(downloadItemIndex === -1 ? undefined : downloadItemIndex);
    setShowPointLevelResultsModal(stepId === "outputTypeModal");
    setShowMoreDropdown(stepId === "moreActions");
    setShowTutorialEditModal(stepId === "editModal");
    setShowTutorialDeleteModal(stepId === "deleteModal");
  }} data-sentry-element="InteractiveTutorial" data-sentry-component="PortfolioInsightsTutorial" data-sentry-source-file="PortfolioInsightsTutorial.tsx" />;
};
export default PortfolioInsightsTutorial;